/**
 * KanbanFieldSelector.js
 * 
 * Dropdown component for selecting a status field with an option to show all.
 * Passes the selected field ID to the parent component via `onChange`.
 * Includes ARIA attributes for accessibility.
 */

import PropTypes from 'prop-types';
import { memo } from "react";
import { FormattedMessage } from "react-intl";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

/**
 * KanbanFieldSelector
 * 
 * A component for selecting a status field.
 * 
 * @param {function} onChange - Function to handle field selection change.
 * @param {number | undefined} selectedFieldId - The ID of the selected field, or undefined if 'Show all' is selected.
 * @param {Array} statusFields - Array of status fields with fieldId and name properties.
 * @param {number} viewId - The ID of the current view.
 */
const KanbanFieldSelector = ({
  onChange = () => { },
  selectedFieldId,
  statusFields,
  viewId,
}) => {
  const SHOW_ALL_VALUE = 0;  // Represents the "Show all" option, mapped to undefined

  // Generates a unique labelId based on viewId to link the label with the select input.
  // This ensures proper accessibility for screen readers by associating the label with the input field.
  //
  //   aria-describedby links the label to the select input for better accessibility.
  //   This ensures that screen readers can read the label correctly in relation to the input.
  //
  //   aria-label provides a clear description of the input's purpose for screen readers.
  //   This is helpful when the label is dynamic, complex, or when additional context is necessary.
  //
  const labelId = `kanban-field-selector-helper-label-${viewId}`;

  const labelText = <FormattedMessage id="common.status" />;

  const handleChange = (e) => {
    const value = e.target.value;
    const fieldId = (value === SHOW_ALL_VALUE) ? undefined : value;
    onChange(fieldId);
  };

  const formControlStyle = {
    mt: 1,
    "& .MuiFormLabel-root, & .MuiInputBase-input": {
      fontSize: "12px",
    },
  };

  return (
    <FormControl sx={formControlStyle}>
      <InputLabel id={labelId}>{labelText}</InputLabel>
      <Select
        aria-describedby={labelId} // Linking the label for accessibility
        aria-label="Select a status field" // Added aria-label for screen readers
        label={labelText}
        labelId={labelId}
        shrink
        size="small"
        onChange={handleChange}
        value={selectedFieldId || SHOW_ALL_VALUE}
      >
        <MenuItem value={SHOW_ALL_VALUE}>
          <em><FormattedMessage id="common.showAll" /></em>
        </MenuItem>
        {statusFields.map((field) => (
          <MenuItem key={field.fieldId} value={field.fieldId}>
            {field.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

KanbanFieldSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  selectedFieldId: PropTypes.oneOf([undefined, PropTypes.number]),
  statusFields: PropTypes.arrayOf(
    PropTypes.shape({
      fieldId: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  viewId: PropTypes.number.isRequired,
};

export default memo(KanbanFieldSelector);
